import { FormActionFrameworkStep, registerStep } from '../step';
import { resolveTemplate } from '../template';
/**
 * Show a message to the user.
 * With a selector: inject a dismissible inline alert near the matched element.
 * Without: traditional window.alert().
 * Blank message (after {{ }} resolve) clears any existing inline alert for the selector
 * and skips window.alert — use this to dismiss a previous alert.
 */
export class AlertStep extends FormActionFrameworkStep {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'alert'
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                message: '',
                selector: '',
            }
        });
    }
    async execute(context) {
        const resolvedMessage = resolveTemplate(this.config.message ?? '', context);
        const message = resolvedMessage == null
            ? ''
            : typeof resolvedMessage === 'object'
                ? JSON.stringify(resolvedMessage)
                : String(resolvedMessage);
        const blank = message.trim() === '';
        const selector = String(resolveTemplate(this.config.selector ?? '', context) ?? '').trim();
        if (!selector) {
            if (blank) {
                context.log('[alert] blank message with no selector — nothing to clear');
                return context;
            }
            if (typeof window !== 'undefined' && typeof window.alert === 'function') {
                window.alert(message);
                context.log(`[alert] window.alert (${message.length} chars)`);
            }
            else {
                context.log('[alert] no window.alert available — skipped');
            }
            return context;
        }
        if (typeof document === 'undefined') {
            context.log('[alert] no DOM — falling back unavailable; skipped');
            return context;
        }
        let target = null;
        try {
            target = document.querySelector(selector);
        }
        catch (err) {
            const errMessage = err instanceof Error ? err.message : String(err);
            context.log(`[alert] invalid selector "${selector}": ${errMessage}`);
            if (!blank &&
                typeof window !== 'undefined' &&
                typeof window.alert === 'function') {
                window.alert(message);
                context.log('[alert] fell back to window.alert after invalid selector');
            }
            return context;
        }
        if (!target) {
            if (blank) {
                context.log(`[alert] blank message; no element matched "${selector}" — nothing to clear`);
                return context;
            }
            context.log(`[alert] no element matched "${selector}" — falling back to window.alert`);
            if (typeof window !== 'undefined' && typeof window.alert === 'function') {
                window.alert(message);
            }
            return context;
        }
        if (blank) {
            const removed = clearInlineAlerts(target);
            context.log(`[alert] blank message — cleared ${removed} inline alert(s) on "${selector}"`);
            return context;
        }
        showInlineAlert(target, message);
        context.log(`[alert] inline alert on "${selector}"`);
        return context;
    }
    showStepForm(container, _context, onUpdated) {
        const cfg = this.config;
        container.innerHTML = `
      <div class="mb-3">
        <label class="form-label">Step name</label>
        <input type="text" class="form-control" data-faf="name" value="${escapeAttr(this.name)}" />
      </div>
      <div class="mb-3">
        <label class="form-label">Message</label>
        <textarea class="form-control" data-faf="message" rows="3" placeholder="Alert text — supports {{ }}">${escapeText(String(cfg.message ?? ''))}</textarea>
        <div class="text-muted small">Leave blank (or resolve to blank) to clear an existing inline alert for the selector.</div>
      </div>
      <div class="mb-3">
        <label class="form-label">Selector (optional)</label>
        <input type="text" class="form-control" data-faf="selector" value="${escapeAttr(cfg.selector)}" placeholder="css_selector or [data-name=&quot;surveyjs_data_name&quot;]" />
        <div class="text-muted small">CSS selector for element.querySelector. Empty → window.alert(). Blank message + selector → clear inline alert.</div>
      </div>
    `;
        const sync = () => {
            this.name = inputValue(container, 'name');
            this.config.message = textareaValue(container, 'message');
            this.config.selector = inputValue(container, 'selector');
            onUpdated?.();
        };
        container.querySelectorAll('input, textarea').forEach((el) => {
            el.addEventListener('change', sync);
            el.addEventListener('blur', sync);
        });
    }
}
function alertHost(target) {
    return target instanceof HTMLElement
        ? target
        : target.parentElement;
}
/** Remove FAF inline alerts under the matched host. Returns how many were removed. */
function clearInlineAlerts(target) {
    const host = alertHost(target);
    if (!host)
        return 0;
    const existing = host.querySelectorAll(':scope > .faf-inline-alert');
    const count = existing.length;
    existing.forEach((el) => el.remove());
    return count;
}
function showInlineAlert(target, message) {
    const host = alertHost(target);
    if (!host)
        return;
    clearInlineAlerts(target);
    const alertEl = document.createElement('div');
    alertEl.className = 'faf-inline-alert';
    alertEl.setAttribute('role', 'alert');
    const text = document.createElement('span');
    text.className = 'faf-inline-alert-text';
    text.textContent = message;
    alertEl.appendChild(text);
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'faf-inline-alert-close';
    close.setAttribute('aria-label', 'Dismiss');
    close.textContent = '×';
    close.addEventListener('click', () => alertEl.remove());
    alertEl.appendChild(close);
    // Prefer inserting before the question content so it sits under the title.
    const content = host.querySelector(':scope > .sd-element__content, :scope > .sd-question__content');
    if (content) {
        content.insertAdjacentElement('beforebegin', alertEl);
    }
    else {
        host.insertAdjacentElement('afterbegin', alertEl);
    }
    host.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}
function inputValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value?.trim() ?? '';
}
function textareaValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value ?? '';
}
function escapeAttr(value) {
    if (value == null)
        return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
}
function escapeText(value) {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;');
}
registerStep('alert', AlertStep);
